import { useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import {
  normalizeCheckboxValue,
  normalizeBooleanValue,
} from '../../../utils/formCrafter/formDataUtils';

/**
 * useFieldHandlers - UI hook that provides standardized handlers for form field interactions
 *
 * @param {Function} onChange - Callback when value changes
 * @param {any} value - Current field value
 * @param {Object} fileConfig - File upload configuration
 * @returns {Object} Field handlers and normalized values
 */
const useFieldHandlers = (onChange, value, fileConfig = {}) => {
  const getValueFromEventOrValue = useCallback((eventOrValue) => {
    if (eventOrValue == null) return '';
    if (
      typeof eventOrValue === 'object' &&
      'target' in eventOrValue &&
      eventOrValue.target &&
      typeof eventOrValue.target === 'object' &&
      'value' in eventOrValue.target
    ) {
      return eventOrValue.target.value ?? '';
    }
    return eventOrValue;
  }, []);

  const handleTextChange = useCallback(
    (eventOrValue) => {
      onChange(getValueFromEventOrValue(eventOrValue));
    },
    [onChange, getValueFromEventOrValue]
  );

  const handleSelectChange = useCallback(
    (eventOrValue) => {
      const extractedValue = getValueFromEventOrValue(eventOrValue);
      onChange(extractedValue);
    },
    [onChange, getValueFromEventOrValue]
  );

  const handleDateChange = useCallback(
    (momentValue) => {
      if (momentValue && momentValue.isValid()) {
        onChange(momentValue.format('YYYY-MM-DD'));
      } else {
        onChange('');
      }
    },
    [onChange]
  );

  const handleDateTimeChange = useCallback(
    (eventOrValue) => {
      onChange(getValueFromEventOrValue(eventOrValue));
    },
    [onChange, getValueFromEventOrValue]
  );

  const handleRadioChange = useCallback(
    (eventOrValue) => {
      onChange(getValueFromEventOrValue(eventOrValue));
    },
    [onChange, getValueFromEventOrValue]
  );

  const handleCheckboxGroupChange = useCallback(
    (eventOrValue) => {
      onChange(getValueFromEventOrValue(eventOrValue));
    },
    [onChange, getValueFromEventOrValue]
  );

  const handleSingleCheckboxChange = useCallback(
    (e, checked) => {
      onChange(checked);
    },
    [onChange]
  );

  const handleSwitchChange = useCallback(
    (e, checked) => {
      onChange(checked);
    },
    [onChange]
  );

  // Ref tracks the latest file value so that rapid sequential calls to
  // handleFileUpload (e.g. when FileUploader fires per-file) always see
  // the accumulated result instead of a stale closure value.
  const fileValueRef = useRef(value);
  fileValueRef.current = value;

  const handleFileUpload = useCallback(
    (newFiles) => {
      if (fileConfig.maxItems === 1) {
        const result = newFiles.slice(0, 1);
        fileValueRef.current = result;
        onChange(result);
      } else {
        const currentFiles = Array.isArray(fileValueRef.current)
          ? fileValueRef.current
          : [];
        const combined = [...currentFiles, ...newFiles];
        const result = combined.slice(0, fileConfig.maxItems);
        fileValueRef.current = result;
        onChange(result);
      }
    },
    [onChange, fileConfig.maxItems]
  );

  const handleFileDelete = useCallback(
    (fileToDelete) => {
      const currentFiles = Array.isArray(fileValueRef.current)
        ? fileValueRef.current
        : [];
      const result = currentFiles.filter((f) => f.name !== fileToDelete.name);
      fileValueRef.current = result;
      onChange(result);
    },
    [onChange]
  );

  const handlePhoneChange = useCallback(
    (phoneValue) => {
      onChange(phoneValue || '');
    },
    [onChange]
  );

  const dateValue = useMemo(() => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m : null;
  }, [value]);

  const checkboxValue = useMemo(() => normalizeCheckboxValue(value), [value]);

  const booleanValue = useMemo(() => normalizeBooleanValue(value), [value]);

  const fileValue = useMemo(() => {
    if (Array.isArray(value)) return value;
    return [];
  }, [value]);

  return {
    handlers: {
      handleTextChange,
      handleSelectChange,
      handleDateChange,
      handleDateTimeChange,
      handleRadioChange,
      handleCheckboxGroupChange,
      handleSingleCheckboxChange,
      handleSwitchChange,
      handleFileUpload,
      handleFileDelete,
      handlePhoneChange,
    },
    normalizedValues: {
      dateValue,
      checkboxValue,
      booleanValue,
      fileValue,
    },
  };
};

export default useFieldHandlers;
